import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Grid } from '@mui/material';

import { ProductTypes } from 'common/types';
import useDebouncedForm from 'common/hooks/useDebouncedForm';

import ContainerBox from 'components/ContainerBox';
import TimePeriodInput from 'components/TimePeriodInput';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import HasFeature from 'components/features/HasFeature';
import { useTranslation } from 'services/localization/useTranslation';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';

const MaintenanceTimeSettings = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				maintenanceTimeMinutes: product?.maintenanceTimeMinutes ?? null,
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	if (product?.set || product?.type === ProductTypes.ACCESS) return null;

	return (
		<HasFeature
			requiredFeature="MAINTENANCE_TIME"
			fallback={(failedChecks) => {
				return !failedChecks.user ? <AddOnPromoBlock addOn="MAINTENANCE_TIME" /> : null;
			}}
		>
			<ContainerBox>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6">
							{t('product.miscellaneousTitle', 'Miscellaneous settings')}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography variant="body1">
							{t('product.maintenanceTimeTitle', 'Maintenance time')}
						</Typography>
						<Typography variant="body1">
							{t(
								'product.maintenanceTimeDescription',
								'The product will be unavailable for maintenance after each booking, for the specified time period',
							)}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<TimePeriodInput
							handleInputChange={(maintenanceTimeMinutes) => editState({ maintenanceTimeMinutes })}
							timePeriodInMinutes={state.maintenanceTimeMinutes ?? 0}
							disabled={isEditingDisabled}
						/>
					</Grid>
				</Grid>
			</ContainerBox>
		</HasFeature>
	);
};

export default MaintenanceTimeSettings;
